import { API } from "../../../config/API";
import { ContentType } from "./fieldConfig";
import { articleLink } from "../../../utils/articleSlug";

// Maps unified form field names to table-specific API field names.
// Only fields that differ from the unified name need to be listed.
const FIELD_MAP: Record<ContentType, Record<string, string>> = {
  news: {
    heading: "head",
    title: "title",
    body: "body",
    author: "author_name",
    image1: "image",
    image2: "image2",
    socialImage: "social_image",
    authorImage: "author_image",
    image1Caption: "image1_caption",
    image1Description: "image1_description",
    image2Description: "image2_description",
    audio: "audio",
    permalink: "permalink",
    tagString: "newsTag",
    categories: "categories",
    subcategories: "Subcategories",
    isHighPriority: "isHighPriority",
    isSecondLeads: "isSecondLeads",
    onlive: "onlive",
    scheduled_at: "scheduled_at",
    metaTitle: "meta_title",
    metaDescription: "meta_description",
    socialEmbed: "social_embed",
    embedType: "embed_type",
  },
  article: {
    heading: "head",
    body: "body",
    author: "author",
    image1: "image1",
    authorImage: "profile",
    permalink: "permalink",
    tagString: "articleTag",
  },
  editorial: {
    heading: "head",
    title: "title",
    body: "body",
    author: "author",
    image1: "image1",
    image2: "image2",
    authorImage: "profile",
    image1Description: "image1_description",
    image2Description: "image2_description",
    image3Description: "image3_description",
    audio: "audio",
    permalink: "permalink",
    tagString: "EditorialTag",
    seriesName: "seriesName",
  },
  vidya: {
    heading: "title",
    body: "Body",
    author: "Author",
    image1: "image1",
    image2: "image2",
    authorImage: "profile",
    image1Description: "image1_description",
    image2Description: "image2_description",
    permalink: "permalink",
    tags: "tags",
    masterid: "masterid",
    scheduled_at: "scheduled_at",
  },
  njayar: {
    heading: "title",
    body: "Body",
    author: "Author",
    image1: "image1",
    image2: "image2",
    authorImage: "profile",
    image1Description: "image1_description",
    image2Description: "image2_description",
    permalink: "permalink",
    tagString: "NjayarTags",
    njayarCategory: "category",
    isHighPriority: "isHighPriority",
    isSecondPriority: "isSecondPriority",
    isThirdPriority: "isThirdPriority",
    masterid: "masterid",
  },
};

// Reverse field map: table-specific -> unified
const REVERSE_FIELD_MAP: Record<
  ContentType,
  Record<string, string>
> = {} as any;
(Object.keys(FIELD_MAP) as ContentType[]).forEach((ct) => {
  REVERSE_FIELD_MAP[ct] = {};
  Object.entries(FIELD_MAP[ct]).forEach(([unified, api]) => {
    REVERSE_FIELD_MAP[ct][api] = unified;
  });
});

// API endpoint configuration per content type
export const API_ENDPOINTS: Record<
  ContentType,
  {
    create: string;
    update: (id: string | number) => string;
    getById: (id: string | number) => string;
    filter: string;
    listRoute: string;
    publish: (id: string | number) => string;
  }
> = {
  news: {
    create: API.CREATENEWS,
    update: (id) => `${API.UPDATE_NEWS}/${id}`,
    getById: (id) => `${API.GET_ALLNEWS_ID}${id}`,
    filter: API.GET_NEWS,
    listRoute: "/Auth/home",
    publish: (id) => `${API.PUBLISH_NEWS}/${id}`,
  },
  article: {
    create: API.ARTICLE,
    update: (id) => `${API.ARTICLE}/${id}`,
    getById: (id) => `${API.ARTICLE}/${id}?populate=*`,
    filter: API.GET_ARTICLE,
    listRoute: "/Auth/Article",
    publish: (id) => `${API.ARTICLE}/${id}`,
  },
  editorial: {
    create: API.EDITORIALS,
    update: (id) => `${API.EDITORIALS}/${id}`,
    getById: (id) => `${API.EDITORIALS}/${id}?populate[0]=tags`,
    filter: API.GET_EDITORIALS,
    listRoute: "/Auth/editorial",
    publish: (id) => `${API.EDITORIALS}/${id}`,
  },
  vidya: {
    create: API.VIDYA_PRABHAADHAM,
    update: (id) => `${API.VIDYA_PRABHAADHAM}/${id}`,
    getById: (id) => `${API.VIDYA_PRABHAADHAM}/${id}?populate=*`,
    filter: API.GET_VIDYA,
    listRoute: "/Auth/VidyaScreeen",
    publish: (id) => `${API.VIDYA_PRABHAADHAM}/${id}`,
  },
  njayar: {
    create: API.NJAYAR_PRABHAADHAM,
    update: (id) => `${API.NJAYAR_PRABHAADHAM}/${id}`,
    getById: (id) => `${API.NJAYAR_PRABHAADHAM}/${id}?populate=*`,
    filter: API.GET_NJYAR,
    listRoute: "/Auth/NjayarScreen",
    publish: (id) => `${API.NJAYAR_PRABHAADHAM}/${id}`,
  },
};

// URL patterns for preview/copy per content type
export const URL_PATTERNS: Record<
  ContentType,
  (id: number | string, permalink: string) => string
> = {
  // Same URL as the news screen: the slug URL when the permalink is usable,
  // otherwise the bare URL (see utils/articleSlug.ts).
  news: (id, permalink) => articleLink(id, { permalink }),
  // Canonical forms the site declares for each route (id only; the permalink
  // was appended as a keyless `&&slug`, and /njayar is not a route at all).
  article: (id) => `${API.APP_URL}todaysarticle?id=${id}`,
  editorial: (id) => `${API.APP_URL}editorial?id=${id}`,
  vidya: (id) => `${API.APP_URL}Prabhaatham?id=${id}`,
  njayar: (id) => `${API.APP_URL}njayarprabhaatham?id=${id}`,
};

/**
 * Transform unified form values into the API payload for a specific content type.
 */
export function buildPayload(
  contentType: ContentType,
  formValues: Record<string, any>,
  extraData: Record<string, any> = {},
): { data: Record<string, any> } {
  const fieldMap = FIELD_MAP[contentType];
  const payload: Record<string, any> = {};

  // Map form fields to API field names
  Object.entries(formValues).forEach(([key, value]) => {
    if (value === undefined) return;
    const apiField = fieldMap[key] || key;
    payload[apiField] = value;
  });

  // Merge extra data (user, publishedAt, etc.)
  Object.entries(extraData).forEach(([key, value]) => {
    payload[key] = value;
  });

  // News-specific: handle priority as boolean split
  if (contentType === "news") {
    const priority = formValues.isHighPriority;
    payload.isHighPriority = priority === 1;
    payload.isSecondLeads = priority === 2;
    // Clean up raw priority value
    delete payload.priority;
  }

  // Njayar-specific: handle priority triple split
  if (contentType === "njayar") {
    const priority = formValues.isHighPriority;
    payload.isHighPriority = priority === 1;
    payload.isSecondPriority = priority === 2;
    payload.isThirdPriority = priority === 3;
    delete payload.priority;
  }

  return { data: payload };
}

/**
 * Transform API response into unified form field values for editing.
 */
export function parseResponse(
  contentType: ContentType,
  apiData: Record<string, any>,
): Record<string, any> {
  const reverseMap = REVERSE_FIELD_MAP[contentType];
  const result: Record<string, any> = {};

  // For Strapi responses, data might be under `attributes`
  const attrs = apiData?.attributes || apiData;

  Object.entries(attrs).forEach(([apiField, value]) => {
    const unifiedField = reverseMap[apiField];
    if (unifiedField) {
      result[unifiedField] = value;
    } else {
      result[apiField] = value;
    }
  });

  // Parse tags from pipe-separated string
  if (contentType === "news" && attrs.newsTag) {
    result.tagString = attrs.newsTag.split("|").filter(Boolean);
  } else if (contentType === "article" && attrs.articleTag) {
    result.tagString = attrs.articleTag.split("|").filter(Boolean);
  } else if (contentType === "editorial" && attrs.EditorialTag) {
    result.tagString = attrs.EditorialTag.split("|").filter(Boolean);
  } else if (contentType === "njayar" && attrs.NjayarTags) {
    result.tagString = attrs.NjayarTags.split("|").filter(Boolean);
  }

  // Parse vidya tags (array of IDs)
  if (contentType === "vidya" && attrs.tags?.data) {
    result.tags = attrs.tags.data.map((t: any) => t.id);
  }

  // Parse njayar category
  if (contentType === "njayar" && attrs.category) {
    result.njayarCategory = attrs.category.split("|").filter(Boolean);
  }

  // Parse news priority
  if (contentType === "news") {
    if (attrs.isHighPriority) result.isHighPriority = 1;
    else if (attrs.isSecondLeads) result.isHighPriority = 2;
    else result.isHighPriority = 3;
  }

  // Parse njayar priority
  if (contentType === "njayar") {
    if (attrs.isHighPriority) result.isHighPriority = 1;
    else if (attrs.isSecondPriority) result.isHighPriority = 2;
    else if (attrs.isThirdPriority) result.isHighPriority = 3;
    else result.isHighPriority = null;
  }

  // Parse news onlive
  if (contentType === "news") {
    result.onlive = attrs.onlive;
  }

  return result;
}

/**
 * Get image fields from API response based on content type
 */
export function parseImages(
  contentType: ContentType,
  apiData: Record<string, any>,
): {
  imageurl1: string;
  imageurl2: string;
  imageurl3: string;
  authorImg: string;
} {
  const attrs = apiData?.attributes || apiData;

  if (contentType === "news") {
    return {
      imageurl1: attrs.image || "",
      imageurl2: attrs.image2 || "",
      imageurl3: attrs.social_image || "",
      authorImg: attrs.author_image || "",
    };
  }

  // article, editorial, vidya, njayar all use image1/image2/profile
  return {
    imageurl1: attrs.image1 || "",
    imageurl2: attrs.image2 || "",
    imageurl3: attrs.profile || "",
    authorImg: attrs.profile || "",
  };
}
